import { UserContext } from "../utils/UserContext";

export class DocumentLinkManager {
  constructor(private client: any) {}

  async findExistingLink(
    userContext: UserContext,
    documentType: string,
    context: {
      linkedEntityId: string;
      linkedEntityType: "APPLICATION" | "GENUINE_STUDENT";
    },
  ): Promise<any | null> {
    const { data: allLinks } = await this.client.models.DocumentLink.list({
      authMode: "userPool",
      authToken: userContext.token,
    });

    if (!allLinks || allLinks.length === 0) {
      return null;
    }

    let existingLinks = allLinks.filter((link: any) => {
      if (link.documentType !== documentType) {
        return false;
      }

      if (context.linkedEntityType === "APPLICATION") {
        if (link.applicationId !== context.linkedEntityId) {
          return false;
        }
      }

      if (userContext.isInstitutionOrAgent) {
        const matches = link.domain === userContext.userDomain;
        return matches;
      }

      return !!link.documentId;
    });

    if (existingLinks.length === 0) {
      return null;
    }

    return existingLinks[0];
  }

  async createLink(
    documentId: string,
    documentType: string,
    fileName: string,
    fileKey: string,
    domain: string,
    applicantId: string | null,
    context: {
      linkedEntityId: string;
      linkedEntityType: "APPLICATION" | "GENUINE_STUDENT";
      institutionId?: string;
    },
    userContext: UserContext,
  ): Promise<void> {
    await this.client.models.DocumentLink.create(
      {
        userId: userContext.identityId,
        documentId,
        documentType,
        fileName,
        fileKey,
        uploadedAt: new Date().toISOString(),
        status: "ACTIVE",
        domain,
        applicantId,
        institutionId: context.institutionId || domain,
        ...(context.linkedEntityType === "APPLICATION"
          ? { applicationId: context.linkedEntityId }
          : { genuineStudentId: context.linkedEntityId }),
        linkedAt: new Date().toISOString(),
      },
      { authMode: "userPool", authToken: userContext.token },
    );
  }

  async updateLink(
    oldDocumentId: string,
    newDocumentId: string,
    fileName: string,
    fileKey: string,
    documentType: string,
    userContext: UserContext,
  ): Promise<void> {
    const { data: oldLink } = await this.client.models.DocumentLink.get(
      { id: oldDocumentId },
      { authMode: "userPool", authToken: userContext.token },
    );

    if (!oldLink) {
      throw new Error(
        `DocumentLink with documentId ${oldDocumentId} not found`,
      );
    }

    await this.client.models.DocumentLink.delete(
      { id: oldDocumentId },
      { authMode: "userPool", authToken: userContext.token },
    );

    await this.client.models.DocumentLink.create(
      {
        userId: userContext.identityId,
        documentId: newDocumentId,
        documentType: documentType,
        fileName: fileName,
        fileKey: fileKey,
        uploadedAt: new Date().toISOString(),
        status: "ACTIVE",
        domain: oldLink.domain,
        applicantId: oldLink.applicantId,
        institutionId: oldLink.institutionId,
        ...(oldLink.applicationId && { applicationId: oldLink.applicationId }),
        ...(oldLink.genuineStudentId && {
          genuineStudentId: oldLink.genuineStudentId,
        }),
        linkedAt: new Date().toISOString(),
      },
      { authMode: "userPool", authToken: userContext.token },
    );
  }
}
